<template>
	<div class="login-container">
		<el-card class="login-card">
			<template #header>
				<div class="login-header">
					<h2>CRM 系统登录</h2>
				</div>
			</template>

			<el-form ref="loginFormRef" :model="loginForm" :rules="loginRules" label-position="top"
				@keyup.enter="handleLogin">
				<el-form-item prop="username" label="用户名">
					<el-input v-model="loginForm.username" placeholder="请输入用户名" prefix-icon="User" />
				</el-form-item>

				<el-form-item prop="password" label="密码">
					<el-input v-model="loginForm.password" type="password" placeholder="请输入密码" prefix-icon="Lock"
						show-password />
				</el-form-item>

				<el-form-item>
					<el-checkbox v-model="loginForm.remember">记住我</el-checkbox>
				</el-form-item>

				<el-form-item>
					<el-button type="primary" :loading="loading" class="login-button" @click="handleLogin">
						登录
					</el-button>
				</el-form-item>
			</el-form>
		</el-card>
	</div>
</template>

<script>
	import {
		ref,
		reactive,
		onMounted
	} from 'vue'
	import {
		useRouter
	} from 'vue-router'
	import {
		ElMessage
	} from 'element-plus'
	import {
		login
	} from '@/api/auth'
	import {
		useStore
	} from 'vuex'

	export default {
		name: 'LoginView',
		setup() {
			const router = useRouter()
			const store = useStore()
			const loginFormRef = ref(null)
			const loading = ref(false)

			const loginForm = reactive({
				username: '',
				password: '',
				remember: false
			})

			const loginRules = {
				username: [{
						required: true,
						message: '请输入用户名',
						trigger: 'blur'
					},
					{
						min: 3,
						max: 20,
						message: '长度在 3 到 20 个字符',
						trigger: 'blur'
					}
				],
				password: [{
						required: true,
						message: '请输入密码',
						trigger: 'blur'
					},
					{
						min: 6,
						max: 20,
						message: '长度在 6 到 20 个字符',
						trigger: 'blur'
					}
				]
			}

			const handleLogin = async () => {
				if (!loginFormRef.value) return

				try {
					await loginFormRef.value.validate()
					loading.value = true

					const res = await login({
						username: loginForm.username,
						password: loginForm.password
					})

					// 存储token
					localStorage.setItem('token', res.data)

					// 如果选择了记住我，存储用户名
					if (loginForm.remember) {
						localStorage.setItem('username', loginForm.username)
					} else {
						localStorage.removeItem('username')
					}

					// 获取并存储用户信息
					await store.dispatch('fetchUserInfo')

					ElMessage.success('登录成功')
					router.push('/customers')
				} catch (error) {
					console.error('登录失败:', error)
					ElMessage.error(error.response?.data?.message || '登录失败，请检查用户名和密码')
				} finally {
					loading.value = false
				}
			}

			// 如果之前记住了用户名，自动填充
			onMounted(() => {
				const savedUsername = localStorage.getItem('username')
				if (savedUsername) {
					loginForm.username = savedUsername
					loginForm.remember = true
				}
			})

			return {
				loginForm,
				loginFormRef,
				loginRules,
				loading,
				handleLogin
			}
		}
	}
</script>

<style scoped>
	.login-container {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		width: 100vw;
		height: 100vh;
		display: flex;
		justify-content: center;
		align-items: center;
		background: linear-gradient(135deg, #1890ff 0%, #36cfc9 100%);
		z-index: 99999;
		overflow: hidden;
	}

	.login-card {
		width: 100%;
		max-width: 400px;
		margin: 20px;
		position: relative;
		z-index: 100000;
		background: rgba(255, 255, 255, 0.98);
		backdrop-filter: blur(10px);
		border: none;
		box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
		border-radius: 12px;
		padding: 30px;
		animation: slideUp 0.5s ease-out;
	}

	.login-container::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: rgba(0, 0, 0, 0.1);
		z-index: 99998;
	}

	:deep(.el-main) {
		position: relative;
		z-index: 99997;
	}

	:deep(.el-container) {
		position: relative;
		z-index: 99996;
	}

	@keyframes slideUp {
		from {
			opacity: 0;
			transform: translateY(30px);
		}

		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	:deep(.el-form) {
		position: relative;
		z-index: 100001;
	}

	:deep(.el-input__wrapper),
	:deep(.el-button),
	:deep(.el-checkbox) {
		position: relative;
		z-index: 100002;
	}

	.login-header {
		text-align: center;
		padding: 20px 0 30px;
	}

	.login-header h2 {
		margin: 0;
		color: #1890ff;
		font-weight: 600;
		font-size: 28px;
		background: linear-gradient(90deg, #1890ff 0%, #36cfc9 100%);
		-webkit-background-clip: text;
		-webkit-text-fill-color: transparent;
	}

	.login-button {
		width: 100%;
		height: 44px;
		font-size: 16px;
		font-weight: 500;
		background: linear-gradient(90deg, #1890ff 0%, #36cfc9 100%);
		border: none;
		border-radius: 8px;
		transition: all 0.3s ease;
		margin-top: 10px;
	}

	.login-button:hover {
		transform: translateY(-2px);
		box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
	}

	:deep(.el-form-item__label) {
		font-weight: 500;
		color: #606266;
		font-size: 14px;
	}

	:deep(.el-input__wrapper) {
		box-shadow: 0 0 0 1px #dcdfe6 inset;
		border-radius: 8px;
		transition: all 0.3s ease;
		padding: 8px 12px;
	}

	:deep(.el-input__wrapper:hover) {
		box-shadow: 0 0 0 1px #1890ff inset;
	}

	:deep(.el-input__wrapper.is-focus) {
		box-shadow: 0 0 0 1px #1890ff inset;
	}

	:deep(.el-checkbox__label) {
		color: #606266;
	}

	:deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
		background-color: #1890ff;
		border-color: #1890ff;
	}

	:deep(.el-checkbox__input.is-checked + .el-checkbox__label) {
		color: #1890ff;
	}

	@media screen and (max-width: 480px) {
		.login-card {
			margin: 15px;
			padding: 20px;
			max-width: calc(100% - 30px);
		}

		.login-header h2 {
			font-size: 24px;
		}

		.login-button {
			height: 40px;
			font-size: 15px;
		}
	}

	@media screen and (max-height: 600px) {
		.login-container {
			height: 100%;
			min-height: 100vh;
		}

		.login-card {
			margin: 10px;
			padding: 15px;
		}
	}
</style>